import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS_TABLE,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TABLE,
} from 'common/dataTableConstants';

import useBranch from 'client/hooks/useBranch';
import ListingActions from 'client/actions/Listing';

import SrBox from 'client/components/SrBox';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrSunSpotEducationCard from 'client/components/SrSunSpotEducationCard/SrSunSpotEducationCard';
import SrPriceMarketFitSunSpotChart from 'client/components/SrPriceMarketFitSunSpot/SrPriceMarketFitSunSpotChart';
import SrPriceMarketFitSunSpotPartnerTask from 'client/components/SrPriceMarketFitSunSpot/SrPriceMarketFitSunSpotPartnerTask';

const formatWeeklyLeads = ({ listing, weeklyLeads }) => {
  const sunSpotArea = listing.avgLeadsComposition * 0.3;

  const data = weeklyLeads.reduce((memo, week) => {
    return [
      ...memo,
      {
        ...week,
        sunSpot: sunSpotArea,
        cold: week.sunSpotPoint - sunSpotArea / 2,
      },
    ];
  }, []);

  return data;
};

const getDataTableSettingsFromProps = (props) => {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS_TABLE,
    rootViewId: DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TABLE,
    viewId: `${DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TABLE}_${props.listing.id}`,
    initialViewSettings: {
      filter: {
        listingId: props.listing.id,
      },
      customParams: {
        partnerListingsTable: {
          listingId: props.listing.id,
        },
      },
    },
    count: 1,
    itemsPropName: 'listingStats',
  };

  return dataTableSettings;
};

const SrPriceMarketFitSunSpot = ({ listing, currentUser, pricePartnerTask }) => {
  const { dailyPastSevenDaysLeads, priceChanges, dispatch } = useBranch({
    dailyPastSevenDaysLeads: ['listings', 'dailyPastSevenDaysLeads', listing.id],
    priceChanges: ['listings', 'priceChanges', listing.id],
  });

  useEffect(() => {
    dispatch(ListingActions.getDailyPastSevenDaysLeads, { listingId: listing.id });
    dispatch(ListingActions.getPriceChanges, { listingId: listing.id });
  }, [listing, dispatch]);

  if (!dailyPastSevenDaysLeads || !priceChanges) {
    return <div className="loading-gif clearfix" />;
  }

  return (
    <SrDataTableConnected {...getDataTableSettingsFromProps({ listing })}>
      {({ listingStats, onInfiniteScrollWrapperRef }) => {
        const listingStat = listingStats[0];
        return (
          <SrBox
            overflow="hidden"
            borderRadius="14px"
            boxShadow="0px 5px 21px rgba(25, 51, 64, 0.05)"
            ref={onInfiniteScrollWrapperRef}
          >
            {listingStat && <SrSunSpotEducationCard listingStat={listingStat} />}
            <SrPriceMarketFitSunSpotChart
              listing={listing}
              listingStat={listingStat}
              weeklyLeads={formatWeeklyLeads({
                listing,
                weeklyLeads: dailyPastSevenDaysLeads,
              })}
              priceChanges={priceChanges}
            />
            {pricePartnerTask && (
              <SrPriceMarketFitSunSpotPartnerTask
                dispatch={dispatch}
                partnerTask={pricePartnerTask}
                currentUser={currentUser}
                listing={listing}
              />
            )}
          </SrBox>
        );
      }}
    </SrDataTableConnected>
  );
};

SrPriceMarketFitSunSpot.propTypes = {
  listing: PropTypes.object.isRequired,
  pricePartnerTask: PropTypes.object,
};

SrPriceMarketFitSunSpot.displayName = 'SrPriceMarketFitSunSpot';

export default SrPriceMarketFitSunSpot;
